import { create } from 'zustand';
import type { FlowLayoutSlot, FlowListLayoutId } from '@shared/flow/flow-layouts';

/**
 * Session-only store for a slot's (table or page) unsaved ("Modified") layout pick.
 * Never persisted; a null pick deletes the key rather than storing null.
 */
interface State {
  bySlot: Partial<Record<FlowLayoutSlot, FlowListLayoutId>>;
  /**
   * Per slot, bumped on EVERY `setWorkingFlowLayout` call (a pick, a clear, a
   * commit's clear). A caller that awaits something (a view save) captures it
   * before and commits only if it is unchanged after: the rep chose again in
   * between otherwise.
   */
  versionBySlot: Partial<Record<FlowLayoutSlot, number>>;
  /**
   * Per slot, how many view saves that PIN a layout are in flight (a counter:
   * writes can overlap). While one is, a pick of the baseline layout is kept
   * as an explicit working pick instead of collapsing to null, because the
   * save's response is about to install a new view pin that a null pick would
   * lose to. Never bumps `versionBySlot`.
   */
  pinWritesBySlot: Partial<Record<FlowLayoutSlot, number>>;
}

export const initialState: State = {
  bySlot: {},
  versionBySlot: {},
  pinWritesBySlot: {},
};

// Data-only state: tests reset with `setState(initialState, true)`, which replaces
// the whole state, so no actions live in it. Mutation goes through setState below.
export const useFlowLayoutWorkingStore = create<State>(() => ({ ...initialState }));

export function setWorkingFlowLayout(slot: FlowLayoutSlot, id: FlowListLayoutId | null): void {
  useFlowLayoutWorkingStore.setState((s) => {
    const next = { ...s.bySlot };
    if (id === null) delete next[slot];
    else next[slot] = id;
    return { bySlot: next, versionBySlot: { ...s.versionBySlot, [slot]: (s.versionBySlot[slot] ?? 0) + 1 } };
  });
}

/** Opens a pin-write bracket for `slot` (pair with `endFlowLayoutPinWrite` in a `finally`). */
export function beginFlowLayoutPinWrite(slot: FlowLayoutSlot): void {
  useFlowLayoutWorkingStore.setState((s) => ({
    pinWritesBySlot: { ...s.pinWritesBySlot, [slot]: (s.pinWritesBySlot[slot] ?? 0) + 1 },
  }));
}

/** Closes one pin-write bracket for `slot`; floors at zero and drops the key there. */
export function endFlowLayoutPinWrite(slot: FlowLayoutSlot): void {
  useFlowLayoutWorkingStore.setState((s) => {
    const next = { ...s.pinWritesBySlot };
    const count = (next[slot] ?? 0) - 1;
    if (count > 0) next[slot] = count;
    else delete next[slot];
    return { pinWritesBySlot: next };
  });
}

/** True while a pin-carrying save for `slot` is in flight. */
export function flowLayoutPinWriteInFlight(slot: FlowLayoutSlot): boolean {
  return (useFlowLayoutWorkingStore.getState().pinWritesBySlot[slot] ?? 0) > 0;
}

/** The slot's current pick version; 0 until its first `setWorkingFlowLayout`. */
export function workingFlowLayoutVersion(slot: FlowLayoutSlot): number {
  return useFlowLayoutWorkingStore.getState().versionBySlot[slot] ?? 0;
}

export function useWorkingFlowLayout(slot: FlowLayoutSlot): FlowListLayoutId | null {
  return useFlowLayoutWorkingStore((s) => s.bySlot[slot] ?? null);
}
